import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import DishPage from "./DishPage";
import DrinksList from "./DrinksList";
import GridPage from "./GridPage";
import { FaChevronLeft, FaChevronRight, FaHome } from "react-icons/fa";
import FloatingCart from "./FloatingCart";
import "./MenuPager.css";

const SWIPE_THRESHOLD_PX = 60; // distancia mínima para cambiar de página
const SWIPE_DOMINANCE = 1.2; // horizontal debe ganarle al vertical

export default function MenuPager({ items = [], onBack }) {
  const pages = useMemo(() => {
    const pages = [];

    const VIEW_MAP = {
      uno: "dish",
      dos: "grid",
      lista: "list",
    };

    const grouped = {};

    items.forEach((item) => {
      if (!grouped[item.category]) {
        grouped[item.category] = {
          viewType: item.viewType,
          items: [],
        };
      }

      grouped[item.category].items.push(item);
    });

    Object.entries(grouped).forEach(([category, data]) => {
      const reactType = VIEW_MAP[data.viewType];

      if (!reactType) return;

      if (reactType === "dish") {
        data.items.forEach((dish) => pages.push({ type: "dish", data: dish }));
        return;
      }

      if (reactType === "grid") {
        chunk(data.items, 2).forEach((group) =>
          pages.push({
            type: "grid",
            title: category,
            data: group,
          }),
        );
        return;
      }

      if (reactType === "list") {
        pages.push({
          type: "list",
          title: category,
          data: data.items,
        });
      }
    });

    return pages;
  }, [items]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [items]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(pages.length - 1, i + 1));
  }, [pages.length]);

  // --- Swipe (solo Dish + Grid) ---
  const touchRef = useRef({
    active: false,
    x: 0,
    y: 0,
    dx: 0,
    dy: 0,
    lock: null, // 'h' | 'v' | null
  });

  const resetTouch = () => {
    touchRef.current.active = false;
    touchRef.current.dx = 0;
    touchRef.current.dy = 0;
    touchRef.current.lock = null;
  };

  if (!pages.length) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>Cargando menú…</div>
    );
  }

  const safeIndex = Math.min(index, pages.length - 1);
  const page = pages[safeIndex];
  const swipeEnabled = page.type === "dish" || page.type === "grid";

  const handleTouchStart = (e) => {
    if (!swipeEnabled) return;
    if (!e.touches || e.touches.length !== 1) return;

    const target = e.target;

    // Si hay modal, no hacer swipe detrás
    if (target?.closest?.(".modal-overlay")) return;

    // Si inicia el gesto sobre un elemento interactivo, no capturamos swipe
    if (target?.closest?.("button, a, input, textarea, select, label")) return;

    const t = e.touches[0];
    touchRef.current.active = true;
    touchRef.current.x = t.clientX;
    touchRef.current.y = t.clientY;
    touchRef.current.dx = 0;
    touchRef.current.dy = 0;
    touchRef.current.lock = null;
  };

  const handleTouchMove = (e) => {
    if (!swipeEnabled) return;
    if (!touchRef.current.active) return;
    if (!e.touches || e.touches.length !== 1) return;

    const t = e.touches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;

    touchRef.current.dx = dx;
    touchRef.current.dy = dy;

    // Lock de dirección después de un mini movimiento
    if (!touchRef.current.lock) {
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absX > 10 || absY > 10) {
        touchRef.current.lock = absX > absY ? "h" : "v";
      }
    }
  };

  const handleTouchEnd = () => {
    if (!swipeEnabled) return;
    if (!touchRef.current.active) return;

    const dx = touchRef.current.dx;
    const dy = touchRef.current.dy;

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    // Solo si fue claramente horizontal
    if (
      touchRef.current.lock === "h" &&
      absX >= SWIPE_THRESHOLD_PX &&
      absX >= absY * SWIPE_DOMINANCE
    ) {
      if (dx < 0 && safeIndex < pages.length - 1) goNext(); // izquierda -> siguiente
      if (dx > 0 && safeIndex > 0) goPrev(); // derecha -> anterior
    }

    resetTouch();
  };

  return (
    <div className={`pager-wrapper ${swipeEnabled ? "swipe-enabled" : ""}`}>
      {/* ✅ Swipe SOLO aquí (contenido), para que NO afecte la barra inferior */}
      <div
        className="pager-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {page.type === "dish" && (
          <DishPage key={page.data.id} dish={page.data} />
        )}
        {page.type === "grid" && (
          <GridPage key={safeIndex} title={page.title} items={page.data} />
        )}
        {page.type === "list" && (
          <DrinksList key={safeIndex} title={page.title} bebidas={page.data} />
        )}
      </div>

      {/* 🔥 BARRA INFERIOR (se queda igual y funcional) */}
      <div className="bottom-nav">
        <div className="nav-left">
          {onBack && (
            <button
              className="nav-btn home-btn"
              onClick={onBack}
              aria-label="Volver"
              title="Volver"
            >
              <FaHome />
            </button>
          )}

          <button
            className="nav-btn"
            onClick={goPrev}
            disabled={safeIndex === 0}
            aria-label="Anterior"
          >
            <FaChevronLeft />
          </button>
        </div>

        <div id="add-btn-slot" />

        <div className="nav-right">
          <FloatingCart inline />
          <button
            className="nav-btn"
            onClick={goNext}
            disabled={safeIndex === pages.length - 1}
            aria-label="Siguiente"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}

function chunk(arr, size) {
  const res = [];
  for (let i = 0; i < arr.length; i += size) res.push(arr.slice(i, i + size));
  return res;
}
